import { Spinner } from "@via/components/loading/loading";
import { useState } from "react";
import RevisionCommentModal, {
  ModalType,
} from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/revision-comment-modal";
import { SaveProcessMode } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";

const CancelButton = ({ onCancel }) => (
  <button
    id="cancel-button"
    type="button"
    className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
    onClick={() => onCancel()}>
    Cancel
  </button>
);

const CreateProcessButton = ({ onSaveProcess, draft, deploymentConfig }) => {
  const { setNotification } = useNotifications();

  const validateAutoDeploy = (): boolean => {
    if (
      deploymentConfig?.autoDeployEnabled === true &&
      !deploymentConfig?.autoDeployConfig
    ) {
      setNotification({
        type: "error",
        message:
          "Auto Deploy is enabled but deployment configuration is not set. Please set a deployment configuration or disable Auto Deploy.",
      });
      return false;
    }
    return true;
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!validateAutoDeploy()) {
      return;
    }
    const payload = {
      ...draft,
      ...(deploymentConfig?.autoDeployEnabled !== undefined && {
        autoDeployEnabled: deploymentConfig?.autoDeployEnabled,
      }),
      ...(deploymentConfig?.autoUndeployEnabled !== undefined && {
        autoUndeployEnabled: deploymentConfig?.autoUndeployEnabled,
      }),
      ...(deploymentConfig?.autoDeployConfig !== undefined && {
        autoDeployConfig: deploymentConfig?.autoDeployConfig,
      }),
    };
    onSaveProcess(payload);
  };

  return (
    <button
      id="create-process-button"
      type="button"
      onClick={handleClick}
      className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
      <Spinner>Create Process</Spinner>
    </button>
  );
};

const SelectRevisionButton = ({ onChangeRevision, rawData, isDisabled }) => (
  <button
    id="select-revision-button"
    type="button"
    onClick={e => {
      e.preventDefault();
      onChangeRevision(rawData);
    }}
    disabled={isDisabled}
    className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
    Select Revision
  </button>
);

const SaveAndLeaveProcessButton = ({
  id,
  isLoading,
  isDisabled,
  setRevisionCommentModalType,
  setSelectedSaveMode,
  mode,
}) => {
  return (
    <button
      id={id}
      type="button"
      onClick={e => {
        e.preventDefault();
        setSelectedSaveMode(mode);
        setRevisionCommentModalType(ModalType.OVERWRITE_REVISION);
      }}
      disabled={isDisabled}
      className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
      <Spinner loading={isLoading}>{mode}</Spinner>
    </button>
  );
};

export default function Footer({
  canEditProcess,
  isCreateProcessRevisionLoading,
  isOverwriteRevisionLoading,
  onCancel,
  draft,
  rawData,
  onSaveProcess,
  onChangeRevision,
  onOverwriteProcess,
  isRevisionChange,
  hideSelectRevision = false,
  deploymentConfig,
  selectedModelInputs,
}) {
  const [revisionCommentModalType, setRevisionCommentModalType] = useState("");
  const [selectedSaveMode, setSelectedSaveMode] = useState("");
  const { setNotification } = useNotifications();

  const validateAutoDeploy = (): boolean => {
    if (
      deploymentConfig?.autoDeployEnabled === true &&
      !deploymentConfig?.autoDeployConfig
    ) {
      setNotification({
        type: "error",
        message:
          "Auto Deploy is enabled but deployment configuration is not set. Please set a deployment configuration or disable Auto Deploy.",
      });
      return false;
    }
    return true;
  };

  const handleRevisionModalConfirm = async (
    revisionComment: string,
    modalType: string
  ) => {
    if (!validateAutoDeploy()) {
      return;
    }
    const payload = {
      ...draft,
      revisionComment,
      ...(deploymentConfig?.autoDeployEnabled !== undefined && {
        autoDeployEnabled: deploymentConfig?.autoDeployEnabled,
      }),
      ...(deploymentConfig?.autoUndeployEnabled !== undefined && {
        autoUndeployEnabled: deploymentConfig?.autoUndeployEnabled,
      }),
      ...(selectedModelInputs &&
        selectedModelInputs.length > 0 && {
          selectedModelInputs,
        }),
    };
    if (modalType === ModalType.CREATE_REVISION) {
      await onSaveProcess(payload);
    } else {
      await onOverwriteProcess(
        payload,
        selectedSaveMode,
        deploymentConfig?.autoDeployConfig
      );
    }
    setRevisionCommentModalType("");
  };

  return (
    <>
      {revisionCommentModalType && (
        <RevisionCommentModal
          type={revisionCommentModalType}
          setOpen={() => setRevisionCommentModalType("")}
          draft={draft}
          isLoading={isOverwriteRevisionLoading}
          onConfirm={handleRevisionModalConfirm}
        />
      )}
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <>
          {draft?.isNewProcess ? (
            <CreateProcessButton
              onSaveProcess={onSaveProcess}
              draft={draft}
              deploymentConfig={deploymentConfig}
            />
          ) : (
            <>
              {isRevisionChange && !hideSelectRevision && (
                <SelectRevisionButton
                  onChangeRevision={onChangeRevision}
                  rawData={rawData}
                  isDisabled={
                    isCreateProcessRevisionLoading || isOverwriteRevisionLoading
                  }
                />
              )}
              {canEditProcess && (
                <SaveAndLeaveProcessButton
                  id="save-process-button"
                  isDisabled={
                    isOverwriteRevisionLoading || isCreateProcessRevisionLoading
                  }
                  isLoading={isOverwriteRevisionLoading}
                  setRevisionCommentModalType={setRevisionCommentModalType}
                  setSelectedSaveMode={setSelectedSaveMode}
                  mode={SaveProcessMode.SAVE}
                />
              )}
              {canEditProcess && (
                <SaveAndLeaveProcessButton
                  id="save-and-leave-process-button"
                  isDisabled={
                    isOverwriteRevisionLoading || isCreateProcessRevisionLoading
                  }
                  isLoading={isOverwriteRevisionLoading}
                  setRevisionCommentModalType={setRevisionCommentModalType}
                  setSelectedSaveMode={setSelectedSaveMode}
                  mode={SaveProcessMode.SAVEANDLEAVE}
                />
              )}
            </>
          )}
        </>
        <CancelButton onCancel={onCancel} />
      </div>
    </>
  );
}
